/**
 * Tokenizer for Annotation Editor
 *
 * Tokenization rules:
 * - Word = 1 token
 * - Space = 1 token
 * - Punctuation + trailing space = 1 token combined
 */

export interface Token {
  index: number;
  text: string;
  type: 'word' | 'space' | 'punctuation';
  startChar: number;  // Character position in original string
  endChar: number;
}

/**
 * Tokenize a sentence following annotation rules
 */
export function tokenize(sentence: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  let tokenIndex = 0;

  while (i < sentence.length) {
    const char = sentence[i];
    const startChar = i;

    // Check if it's punctuation
    if (/[.,!?;:'")\]}]/.test(char)) {
      // Punctuation + trailing space = 1 token
      let text = char;
      i++;
      // Include trailing space if exists
      if (i < sentence.length && sentence[i] === ' ') {
        text += ' ';
        i++;
      }
      tokens.push({
        index: tokenIndex++,
        text,
        type: 'punctuation',
        startChar,
        endChar: i,
      });
    }
    // Check if it's a space (not after punctuation)
    else if (char === ' ') {
      tokens.push({
        index: tokenIndex++,
        text: ' ',
        type: 'space',
        startChar,
        endChar: i + 1,
      });
      i++;
    }
    // It's a word
    else {
      let text = '';
      while (i < sentence.length && !/[\s.,!?;:'")\]}]/.test(sentence[i])) {
        text += sentence[i];
        i++;
      }
      if (text) {
        tokens.push({
          index: tokenIndex++,
          text,
          type: 'word',
          startChar,
          endChar: i,
        });
      }
    }
  }

  return tokens;
}

/**
 * Get text for a range of tokens
 */
export function getTokenRangeText(tokens: Token[], startPos: number, endPos: number): string {
  return tokens
    .filter(t => t.index >= startPos && t.index <= endPos)
    .map(t => t.text)
    .join('');
}

/**
 * Parse annotation string
 * Format: "[effect startPos endPos startTime endTime]"
 */
export interface ParsedAnnotation {
  effect: 'underline' | 'highlight' | 'hide_text' | 'insert';
  startPos: number;
  endPos: number;
  startTime: number;
  endTime: number;
  insertId?: number;  // For insert type
}

export function parseAnnotation(annotation: string): ParsedAnnotation | null {
  // Match standard format: [effect startPos endPos startTime endTime]
  const rangeMatch = annotation.match(/\[(underline|highlight|hide_text)\s+(\d+)\s+(\d+)\s+([\d.]+)\s+([\d.]+)\s*\]/);
  if (rangeMatch) {
    return {
      effect: rangeMatch[1] as ParsedAnnotation['effect'],
      startPos: parseInt(rangeMatch[2], 10),
      endPos: parseInt(rangeMatch[3], 10),
      startTime: parseFloat(rangeMatch[4]),
      endTime: parseFloat(rangeMatch[5]),
    };
  }

  // Match insert format: [insert pos startTime endTime insertId]
  const insertMatch = annotation.match(/\[insert\s+(\d+)\s+([\d.]+)\s+([\d.]+)\s+(\d+)\s*\]/);
  if (insertMatch) {
    return {
      effect: 'insert',
      startPos: parseInt(insertMatch[1], 10),
      endPos: parseInt(insertMatch[1], 10),
      startTime: parseFloat(insertMatch[2]),
      endTime: parseFloat(insertMatch[3]),
      insertId: parseInt(insertMatch[4], 10),
    };
  }

  return null;
}

/**
 * Build annotation string from parsed data
 */
export function buildAnnotation(parsed: ParsedAnnotation): string {
  if (parsed.effect === 'insert' && parsed.insertId !== undefined) {
    return `[insert ${parsed.startPos} ${parsed.startTime.toFixed(2)} ${parsed.endTime.toFixed(2)} ${parsed.insertId}]`;
  }
  return `[${parsed.effect} ${parsed.startPos} ${parsed.endPos} ${parsed.startTime.toFixed(2)} ${parsed.endTime.toFixed(2)}]`;
}

/**
 * Effect type display names
 */
export const EFFECT_LABELS: Record<ParsedAnnotation['effect'], string> = {
  underline: '底線',
  highlight: '高亮',
  hide_text: '隱藏',
  insert: '插入',
};

/**
 * Effect type colors
 */
export const EFFECT_COLORS: Record<ParsedAnnotation['effect'], string> = {
  underline: '#3b82f6',  // blue
  highlight: '#f59e0b',  // amber
  hide_text: '#ef4444',  // red
  insert: '#10b981',     // green
};
